import '@fontsource/fraunces/300.css';
import '@fontsource/fraunces/400.css';
import '@fontsource/fraunces/500.css';
import '@fontsource/fraunces/600.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';

import "@/App.css";
import "@/index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { ProductsProvider } from "@/lib/ProductsContext";
import { ContentProvider } from "@/lib/ContentContext";
import { AdminAuthProvider } from "@/lib/AdminAuthContext";
import { TrackerAuthProvider } from "@/tracker/lib/TrackerAuthContext";

// Main bundle — above the fold / high-traffic SEO pages
import HomePage from "@/pages/HomePage";
import SEOPage from "@/pages/SEOPage";
import HubPage from "@/pages/HubPage";
import HubDetailPage from "@/pages/HubDetailPage";
import Layout from "@/components/Layout";

// Lazy-loaded chunks
const BundlePage = lazy(() => import("@/pages/BundlePage"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));
const FreebieLP = lazy(() => import("@/pages/FreebieLP"));
const ToolPage = lazy(() => import("@/pages/ToolPage"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const DisclaimerPage = lazy(() => import("@/pages/DisclaimerPage"));
const RefundPage = lazy(() => import("@/pages/RefundPage"));
const AnalyticsDashboard = lazy(() => import("@/pages/AnalyticsDashboard"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const AdminContentPage = lazy(() => import("@/pages/AdminContentPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const ProtectedAdminRoute = lazy(() => import("@/components/ProtectedAdminRoute"));
const AdminNav = lazy(() => import("@/components/AdminNav"));
const FreeGuidePage = lazy(() => import("@/pages/FreeGuidePage"));
const StartHerePage = lazy(() => import("@/pages/StartHerePage"));
const OperatingSystemPage = lazy(() => import("@/pages/OperatingSystemPage"));
const PKCalculator = lazy(() => import("@/pages/PKCalculator"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const KaiChat = lazy(() => import("@/pages/KaiChat"));
const KaiLanding = lazy(() => import("@/pages/KaiLanding"));
const TrackerLanding = lazy(() => import("@/pages/TrackerLanding"));
const DoseHubPage = lazy(() => import("@/pages/DoseHubPage"));
const AdminKai = lazy(() => import("@/pages/AdminKai"));
const AdminTracker = lazy(() => import("@/pages/AdminTracker"));
const AdminBeta = lazy(() => import("@/pages/AdminBeta"));
const AdminHome = lazy(() => import("@/pages/AdminHome"));
const BetaLandingPage = lazy(() => import("@/pages/BetaLandingPage"));
const BetaThankYou = lazy(() => import("@/pages/BetaThankYou"));

// Tracker module (Phase 1)
const TrackerLayout = lazy(() => import("@/tracker/TrackerLayout"));
const TrackerProtected = lazy(() => import("@/tracker/TrackerProtected"));
const TrackerLogin = lazy(() => import("@/tracker/pages/TrackerLogin"));
const TrackerHome = lazy(() => import("@/tracker/pages/TrackerHome"));
const TrackerLog = lazy(() => import("@/tracker/pages/TrackerLog"));
const TrackerProgress = lazy(() => import("@/tracker/pages/TrackerProgress"));
const TrackerKai = lazy(() => import("@/tracker/pages/TrackerKai"));
const TrackerProtocol = lazy(() => import("@/tracker/pages/TrackerProtocol"));
const TrackerSettings = lazy(() => import("@/tracker/pages/TrackerSettings"));
const ShortcutsPage = lazy(() => import("@/tracker/pages/ShortcutsPage"));
const TrackerAssessment = lazy(() => import("@/tracker/pages/TrackerAssessment"));
const ClinicianReplyPage = lazy(() => import("@/pages/ClinicianReplyPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));

const PageSpinner = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-[#2D6A4F] border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ProductsProvider>
      <ContentProvider>
      <AdminAuthProvider>
      <TrackerAuthProvider>
      <Toaster position="top-center" richColors />
      <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/glp-survival-system" element={<BundlePage />} />
          <Route path="/guides/:guideId" element={<GuidePage />} />
          <Route path="/free/:freebieId" element={<FreebieLP />} />
          <Route path="/glp-nausea-fix" element={<ToolPage toolId="nausea-fix" />} />
          <Route path="/glp-constipation-fix" element={<ToolPage toolId="constipation-fix" />} />
          <Route path="/glp-energy-plateau" element={<ToolPage toolId="energy-plateau" />} />
          <Route path="/glp-muscle-loss" element={<ToolPage toolId="muscle-loss" />} />
          <Route path="/glp-plateau-detector" element={<ToolPage toolId="plateau-detector" />} />
          <Route path="/glp-pk-calculator" element={<PKCalculator />} />
          <Route path="/glp-guide" element={<HubPage />} />
          <Route path="/dose-and-meds" element={<DoseHubPage />} />
          <Route path="/glp-side-effects" element={<HubDetailPage hubId="side-effects" />} />
          <Route path="/glp-food-protein" element={<HubDetailPage hubId="food-protein" />} />
          <Route path="/glp-expectations" element={<HubDetailPage hubId="expectations" />} />
          <Route path="/glp-results" element={<HubDetailPage hubId="results" />} />
          <Route path="/articles/:slug" element={<SEOPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/free-guide" element={<FreeGuidePage />} />
          <Route path="/start-here" element={<StartHerePage />} />
          <Route path="/glp1-operating-system" element={<OperatingSystemPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/meet-kai" element={<KaiLanding />} />
          <Route path="/meet-tracker" element={<TrackerLanding />} />
          <Route path="/beta-test" element={<BetaLandingPage />} />
          <Route path="/beta-thank-you" element={<BetaThankYou />} />
          <Route path="/account" element={<AccountPage />} />
        </Route>
        {/* Kai AI Coach — standalone, no public header/footer */}
        <Route path="/kai" element={<KaiChat />} />
        {/* Admin routes — outside Layout (no public header/footer) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<ProtectedAdminRoute><><AdminNav /><AdminHome /></></ProtectedAdminRoute>} />
        <Route path="/admin/analytics" element={<ProtectedAdminRoute><><AdminNav /><AnalyticsDashboard /></></ProtectedAdminRoute>} />
        <Route path="/admin/content" element={<ProtectedAdminRoute><><AdminNav /><AdminContentPage /></></ProtectedAdminRoute>} />
        <Route path="/admin/kai" element={<ProtectedAdminRoute><><AdminNav /><AdminKai /></></ProtectedAdminRoute>} />
        <Route path="/admin/tracker" element={<ProtectedAdminRoute><><AdminNav /><AdminTracker /></></ProtectedAdminRoute>} />
        <Route path="/admin/beta" element={<ProtectedAdminRoute><><AdminNav /><AdminBeta /></></ProtectedAdminRoute>} />

        {/* Tracker module — mobile-first PWA shell, /tracker/* */}
        <Route path="/tracker" element={<TrackerLayout />}>
          <Route index element={<TrackerLogin />} />
          <Route path="login" element={<TrackerLogin />} />
          <Route path="home" element={<TrackerProtected><TrackerHome /></TrackerProtected>} />
          <Route path="log" element={<TrackerProtected><TrackerLog /></TrackerProtected>} />
          <Route path="progress" element={<TrackerProtected><TrackerProgress /></TrackerProtected>} />
          <Route path="assessments/:id" element={<TrackerProtected><TrackerAssessment /></TrackerProtected>} />
          <Route path="kai" element={<TrackerProtected><TrackerKai /></TrackerProtected>} />
          <Route path="protocol" element={<TrackerProtected><TrackerProtocol /></TrackerProtected>} />
          <Route path="settings" element={<TrackerProtected><TrackerSettings /></TrackerProtected>} />
          <Route path="shortcuts" element={<TrackerProtected><ShortcutsPage /></TrackerProtected>} />
        </Route>

        {/* V12 — public, token-protected clinician reply micro-page (no auth) */}
        <Route path="/clinician/:token" element={<ClinicianReplyPage />} />

        {/* V15 ITEM 1 — public pricing page */}
        <Route path="/pricing" element={<PricingPage />} />
      </Routes>
      </Suspense>
      </TrackerAuthProvider>
      </AdminAuthProvider>
      </ContentProvider>
      </ProductsProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
